import React, { useState, useEffect } from 'react';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import axios from 'axios';
import './App.css';
import { getAccessToken, getAuthHeaders, isDemoMode } from './utils/auth';
import {
  msalInstance,
  loginWithMicrosoft,
  logoutWithMicrosoft,
  isAuthInteractionInProgress,
  isMobileDevice,
} from './utils/msalConfig';
import { formatUsDateFromIso, formatUsDateFromDateTime } from './utils/usDateFormat';
import { formatEmployeeNameWithNo } from './utils/employeeDisplay';

// Import the Organization Chart component
import OrganizationChart from './OrganizationChart';
import RequestManagement from './Pages/LeaveManagement/LeaveManagement';
import EmployeeManagement from './Pages/EmployeeManagement/EmployeeManagement';
import App_Navbar from './components/Navbar/Navbar';
import NotificationCenter from './components/NotificationCenter/NotificationCenter';
import App_Header from './components/Header/Header';
import PositionsManagement from './Pages/PositionsManagement/PositionsManagement';
import ProfilePage from './Pages/profile/profile';
import Loader from './components/Loader/Loader';
import CheckInCheckOut from './Pages/CheckInCheckOut/CheckInCheckOut';
import AllCheckInOut from './Pages/AllCheckInOut/AllCheckInOut';
import Announcements from './Pages/Announcements/Announcements';
import LocationManagement from './Pages/LocationManagement/LocationManagement';
import ShiftsManagement from './Pages/ShiftsManagement/ShiftsManagement';
import GradesManagement from './Pages/GradesManagement/GradesManagement';
import PoliciesTrainings from './Pages/PoliciesTrainings/PoliciesTrainings';
import {
  isHrNotificationType,
  provisionalHrView,
  resolveTargetView,
} from './utils/notificationNavigation';

export { msalInstance } from './utils/msalConfig';

// Utility function for currency formatting
const formatCurrency = (amount) => {
  if (amount == null) return 'KD 0.000';
  return `KD ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
};

// Utility function for role display names
const getRoleDisplayName = (role) => {
  if (role === null || role === undefined) return 'Staff';
  
  // Handle numeric role codes from Dynamics 365
  const roleMap = {
    100000000: 'Staff',
    100000001: 'Manager', 
    100000002: 'Admin',
    100000003: 'Super'
  };
  
  // If it's a number, convert to string and look up
  if (typeof role === 'number') {
    return roleMap[role] || 'Staff';
  }
  
  // If it's already a string, return as is or capitalize first letter
  if (typeof role === 'string') {
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  }
  
  return 'Staff';
};

// Utility function to check if user has admin privileges (admin or super role)
const hasAdminPrivileges = (role) => {
  return role === 'admin' || role === 'super';
};

// Utility function for role colors
const getRoleColor = (role) => {
  const displayName = getRoleDisplayName(role);
  const colorMap = {
    'Staff': 'staff',
    'Manager': 'manager',
    'Admin': 'admin',
    'Super': 'super'
  };
  return colorMap[displayName] || 'staff';
};

// Login button component
const LoginButton = () => {
  const { instance } = useMsal();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleDemoLogin = (role) => {
    // Clear all cached data first
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear browser cache if possible
    if ('caches' in window) {
      caches.keys().then(function(names) {
        names.forEach(function(name) {
          caches.delete(name);
        });
      });
    }
    
    const tokenMap = {
      'admin': 'admin-token',
      'super': 'super-token',
      'manager': 'manager-token', 
      'staff': 'staff-token'
    };
    
    localStorage.setItem('demo-token', tokenMap[role]);
    localStorage.setItem('demo-role', role);
    
    // Force a fresh reload
    window.location.href = window.location.origin;
  };

  const handleLogin = async () => {
    setIsLoggingIn(true);
    try {
      await loginWithMicrosoft(instance);
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card flex flex-col items-center">
        <img src="/seazen-logo.svg" width={80} alt="Seazen HRMS Logo"/>
        <h1>Seazen HRIS</h1>
        <p>Human Resources Information System</p>
        <button
          onClick={handleLogin}
          className="login-button"
          disabled={isLoggingIn}
        >
          {isLoggingIn ? 'Signing in...' : 'Sign in with Microsoft'}
        </button>
        
        {/* <div className="demo-login-section">
          <p>Demo Login (For Testing)</p>
          <div className="demo-buttons">
            <button onClick={() => handleDemoLogin('admin')} className="demo-button admin">
              🔑 Login as HR Admin
            </button>
            <button onClick={() => handleDemoLogin('super')} className="demo-button super">
              ⭐ Login as Super Admin
            </button>
            <button onClick={() => handleDemoLogin('manager')} className="demo-button manager">
              👔 Login as Manager
            </button>
            <button onClick={() => handleDemoLogin('staff')} className="demo-button staff">
              👤 Login as Staff
            </button>
          </div>
          <p className="demo-note">Use these buttons to test different user roles and permissions</p>
        </div> */}
      </div>
    </div>
  );
};

// Certificate Request Management component
const INITIAL_CERTIFICATE_REQUEST = {
  certificate_type: '',
  purpose: '',
};

const CertificateManagement = ({ userProfile }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [newRequest, setNewRequest] = useState({ ...INITIAL_CERTIFICATE_REQUEST });

  useEffect(() => {
    if (!showRequestForm) {
      setNewRequest({ ...INITIAL_CERTIFICATE_REQUEST });
    }
  }, [showRequestForm]);

  useEffect(() => {
  const handler = setTimeout(() => {
    fetchCertificates();
    }, 300);
    return () => clearTimeout(handler);
  }, []);

  const fetchCertificates = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/certificates`, {
        headers
      });
      setCertificates(response.data.certificates || []);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/certificates`, newRequest, {
        headers
      });
      setShowRequestForm(false);
      fetchCertificates();
      alert('Certificate request submitted successfully!');
    } catch (error) {
      console.error('Error submitting certificate request:', error);
      alert('Error submitting request. Please try again.');
    }
  };

  const handleApproveCertificate = async (certificateId, action, comments) => {
    try {
      const headers = await getAuthHeaders();
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${certificateId}/approve`, {
        action,
        comments
      }, {
        headers
      });
      fetchCertificates();
      alert(`Certificate request ${action}d successfully!`);
    } catch (error) {
      console.error('Error processing certificate request:', error);
      alert('Error processing request. Please try again.');
    }
  };

  const getPageTitle = () => {
    switch (userProfile?.role) {
      case 'admin':
      case 'super': return 'All Certificate Requests';
      case 'manager': return 'Team Certificate Requests';
      case 'staff': return 'My Certificate Requests';
      default: return 'Certificate Requests';
    }
  };

  if (loading) {
    return <div className="loading">Loading certificate requests...</div>;
  }

  return (
    <div className="certificate-management">
      <div className="section-header">
        <h2>{getPageTitle()}</h2>
        {userProfile?.role === 'staff' && (
          <button onClick={() => setShowRequestForm(true)} className="add-button">
            Request Certificate
          </button>
        )}
      </div>

      {showRequestForm && (
        <div className="modal">
          <div className="modal-content">
            <h3>Request Certificate</h3>
            <p className="auto-fill-note">📝 Your employee number will be auto-filled</p>
            <form onSubmit={handleSubmitRequest}>
              <div className="form-field">
                <label>Certificate Type *</label>
                <select
                  value={newRequest.certificate_type}
                  onChange={(e) => setNewRequest({...newRequest, certificate_type: e.target.value})}
                  required
                >
                  <option value="">Select Certificate Type</option>
                  <option value="Employment Certificate">Employment Certificate</option>
                  <option value="Salary Certificate">Salary Certificate</option>
                  <option value="Experience Certificate">Experience Certificate</option>
                  <option value="No Objection Certificate">No Objection Certificate</option>
                  <option value="Training Certificate">Training Certificate</option>
                </select>
              </div>
              
              <div className="form-field">
                <label>Purpose *</label>
                <textarea
                  placeholder="Enter the purpose of this certificate request"
                  value={newRequest.purpose}
                  onChange={(e) => setNewRequest({...newRequest, purpose: e.target.value})}
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button">Submit Request</button>
                <button 
                  type="button" 
                  onClick={() => setShowRequestForm(false)} 
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="certificate-grid leave-grid">
        {certificates.map((certificate) => (
          <div key={certificate.id} className="leave-card">
            <div className="leave-header">
              <h3>{certificate.certificate_type}</h3>
              <span className={`status ${certificate.status.toLowerCase()}`}>
                {certificate.status}
              </span>
            </div>
            <p><strong>Employee:</strong> {formatEmployeeNameWithNo(certificate.employee_name, certificate.employee_no)}</p>
            <p><strong>Purpose:</strong> {certificate.purpose}</p>
            <p><strong>Requested:</strong> {formatUsDateFromDateTime(certificate.requested_date) || formatUsDateFromIso(certificate.requested_date) || '—'}</p>
            
            {certificate.approved_by && (
              <p><strong>Processed by:</strong> {certificate.approved_by}</p>
            )}
            
            {certificate.manager_comments && (
              <p><strong>Manager Comments:</strong> {certificate.manager_comments}</p>
            )}
            
            {certificate.status === 'Pending' && (userProfile?.role === 'manager' || hasAdminPrivileges(userProfile?.role)) && (
              <div className="leave-actions">
                <button 
                  onClick={() => {
                    const comments = prompt('Add comments (optional):');
                    handleApproveCertificate(certificate.id, 'approve', comments);
                  }} 
                  className="approve-button"
                >
                  Approve
                </button>
                <button 
                  onClick={() => {
                    const comments = prompt('Add rejection reason:');
                    handleApproveCertificate(certificate.id, 'reject', comments);
                  }} 
                  className="reject-button"
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {certificates.length === 0 && (
        <div className="no-data">
          <p>No certificate requests found.</p>
        </div>
      )}
    </div>
  );
};

// Dashboard component  
const Dashboard = ({ userProfile }) => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [hrCategoryFilter, setHrCategoryFilter] = useState('all');
  const [adminCategoryFilter, setAdminCategoryFilter] = useState('all');

  useEffect(() => {
     const handler = setTimeout(() => {
      if (userProfile) {
        fetchDashboardStats();
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [userProfile]);

  const fetchDashboardStats = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats`, { headers });

      const dashboard = response.data.dashboard || {};
      const role = response.data.user_role;

      // Base structure
      const normalizedStats = {
        user_role: role,
        system: response.data.system,
        total_employees: dashboard.total_employees,
        total_payslips: dashboard.total_payslips,
        total_hr_requests: dashboard.total_hr_requests,
        pending_hrrequests_total: dashboard.pending_hrrequests_total,
        pending_hrrequests_leave: dashboard.pending_hrrequests_leave,
        pending_hrrequests_admin: dashboard.pending_hrrequests_admin,
        hr_requests_by_category: dashboard.hr_requests_by_category || {
          all: 0,
          categories: [],
          leave_types: [],
        },
        admin_requests_by_category: dashboard.admin_requests_by_category || { all: 0, categories: [] },
        role_specific: {}
      };

      // Admin/Super-specific
      if (role === 'admin' || role === 'super') {
        normalizedStats.role_specific.active_employees = dashboard.system_overview?.active_employees ?? 0;
      }

      // Manager-specific
      if (role === 'manager') {
        normalizedStats.total_employees = dashboard.my_team_member_count ?? 0;
        normalizedStats.pending_hrrequests_leave = dashboard.my_pending_leave_requests ?? 0;
        normalizedStats.pending_hrrequests_admin = dashboard.my_pending_admin_requests ?? 0;
        normalizedStats.pending_hrrequests_total = dashboard.my_pending_hr_requests ?? 0;
        normalizedStats.total_payslips = dashboard.team_payslips ?? 0;
        normalizedStats.role_specific.team_leaves = dashboard.my_team_pending_hr_requests ?? 0;
      }

      // Staff-specific
      if (role === 'staff') {
        normalizedStats.total_payslips = dashboard.my_payslips ?? 0;
        normalizedStats.pending_hrrequests_leave = dashboard.my_pending_leave_requests ?? 0;
        normalizedStats.pending_hrrequests_admin = dashboard.my_pending_admin_requests ?? 0;
        normalizedStats.pending_hrrequests_total = dashboard.my_pending_hr_requests ?? 0;
        normalizedStats.role_specific.current_month_leaves = dashboard.current_month_leaves ?? 0;
      }

      setStats(normalizedStats);
      setHrCategoryFilter('all');
      setAdminCategoryFilter('all');
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const resolveCategoryCount = (breakdown, selected) => {
    if (!breakdown) return 0;
    if (!selected || selected === 'all') return breakdown.all || 0;
    if (selected.startsWith('leave_type:')) {
      const leaveTypeName = selected.slice('leave_type:'.length);
      const match = (breakdown.leave_types || []).find((item) => item.name === leaveTypeName);
      return match?.count ?? 0;
    }
    const match = (breakdown.categories || []).find((item) => item.name === selected);
    return match?.count ?? 0;
  };

  const buildHrDropdownOptions = (breakdown) => {
    const categories = breakdown?.categories || [];
    const leaveTypes = breakdown?.leave_types || [];
    return [
      ...categories.map((item) => ({
        value: item.name,
        label: item.name,
        group: 'category',
      })),
      ...leaveTypes.map((item) => ({
        value: `leave_type:${item.name}`,
        label: item.name,
        group: 'leave_type',
      })),
    ];
  };

  const getDashboardContent = () => {
    switch (userProfile?.role || stats.user_role) {
      case 'admin':
      case 'super':
        return {
          title: `${stats.system || 'Seazen HRMS'} Admin Panel`,
          subtitle: 'Manage Your HR Operations',
          stats: [
            { label: 'Total Employees', value: stats.total_employees || 0, icon: '👥', color: 'bg-blue-600', bgColor: 'bg-blue-100', view: 'employees' },
            {
              label: 'HR Requests',
              value: resolveCategoryCount(stats.hr_requests_by_category, hrCategoryFilter),
              icon: '📋',
              color: 'bg-purple-600',
              bgColor: 'bg-purple-100',
              view: 'requests',
              categoryFilterKey: 'hr',
              categoryOptions: buildHrDropdownOptions(stats.hr_requests_by_category),
              selectedCategory: hrCategoryFilter,
              useGroupedOptions: true,
            },
            {
              label: 'Admin Requests',
              value: resolveCategoryCount(stats.admin_requests_by_category, adminCategoryFilter),
              icon: '🗂️',
              color: 'bg-indigo-600',
              bgColor: 'bg-indigo-100',
              view: 'admin_requests',
              categoryFilterKey: 'admin',
              categoryOptions: (stats.admin_requests_by_category?.categories || []).map((item) => ({
                value: item.name,
                label: item.name,
              })),
              selectedCategory: adminCategoryFilter,
            },
            { label: 'Pending Leave Requests', value: stats.pending_hrrequests_leave || 0, icon: '📅', color: 'bg-yellow-600', bgColor: 'bg-yellow-100', view: 'requests' },
            { label: 'Pending Admin Requests', value: stats.pending_hrrequests_admin || 0, icon: '⏳', color: 'bg-red-600', bgColor: 'bg-red-100', view: 'admin_requests' },
            { label: 'Total Payslips', value: stats.total_payslips || 0, icon: '💰', color: 'bg-green-600', bgColor: 'bg-green-100' },
          ]
        };
      case 'manager':
        return {
          title: `${stats.system || 'Seazen HRMS'} Manager Hub`,
          subtitle: 'Oversee Your Team',
          stats: [
            { label: 'Team Members', value: stats.total_employees || 0, icon: '👥', color: 'bg-blue-600', bgColor: 'bg-blue-100', view: 'employees' },
            { label: 'Pending Team Leaves', value: stats.role_specific?.team_leaves || 0, icon: '🌴', color: 'bg-purple-600', bgColor: 'bg-purple-100', view: 'my_team_requests' },
            { label: 'My Pending Leaves', value: stats.pending_hrrequests_leave || 0, icon: '📅', color: 'bg-yellow-600', bgColor: 'bg-yellow-100', view: 'requests' },
            { label: 'Pending Admin Requests', value: stats.pending_hrrequests_admin || 0, icon: '⏳', color: 'bg-red-600', bgColor: 'bg-red-100', view: 'admin_requests' },
            { label: 'Payslips', value: stats.total_payslips || 0, icon: '💰', color: 'bg-green-600', bgColor: 'bg-green-100' }
          ]
        };
      case 'staff':
        return {
          title: `My ${stats.system || 'Seazen HRMS'} Dashboard`,
          subtitle: 'Your Personal Overview',
          stats: [
            { label: 'My Profile', icon: '👤', color: 'bg-blue-300', bgColor: 'bg-blue-100', view: 'employees' },
            { label: 'Pending Leave Requests', value: stats.pending_hrrequests_leave || 0, icon: '📅', color: 'bg-yellow-600', bgColor: 'bg-yellow-100', view: 'requests' },
            { label: 'Pending Admin Requests', value: stats.pending_hrrequests_admin || 0, icon: '⏳', color: 'bg-red-600', bgColor: 'bg-red-100', view: 'admin_requests' },
            { label: 'Pending HR Requests', value: stats.pending_hrrequests_total || 0, icon: '📝', color: 'bg-blue-600', bgColor: 'bg-blue-100', view: 'requests' },
            { label: 'Payslips', value: stats.total_payslips || 0, icon: '💰', color: 'bg-green-600', bgColor: 'bg-green-100' }
          ]
        };
      default:
        return {
          title: `Welcome to ${stats.system || 'Seazen HRMS'}`,
          subtitle: 'Overview',
          stats: []
        };
    }
  };

  const dashboardContent = getDashboardContent();

  const dashboardShortcuts = [
    { label: 'Check In/Out', view: 'checkInOut', icon: '🕐' },
    { label: 'My Leave Requests', view: 'my_leave_requests', icon: '📅' },
    { label: 'My Admin Requests', view: 'my_admin_requests', icon: '📝' },
  ];

  const handleNavigate = (view) => {
    if (view) {
      window.dispatchEvent(new CustomEvent('navigate', { detail: view }));
    }
  };

  const handleCategoryFilterChange = (filterKey, value) => {
    if (filterKey === 'hr') {
      setHrCategoryFilter(value);
      return;
    }
    if (filterKey === 'admin') {
      setAdminCategoryFilter(value);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 sm:p-8">
      <div className="max-w-7xl mx-auto min-w-0">
        {/* Header */}
        <div className="bg-[#674936] rounded-2xl shadow-xl p-5 sm:p-8 mb-6 sm:mb-8 text-white">
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 min-w-0">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold break-words">{dashboardContent.title}</h1>
              <p className="text-base sm:text-lg mt-2 opacity-90 break-words">{dashboardContent.subtitle}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold bg-white text-[#674936]`}>
                {stats.user_role?.toUpperCase() || 'STAFF'}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Shortcuts */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {dashboardShortcuts.map((shortcut) => (
            <button
              key={shortcut.view}
              type="button"
              onClick={() => handleNavigate(shortcut.view)}
              className="flex items-center justify-center gap-3 min-w-0 bg-white rounded-xl p-4 sm:p-5 shadow-md border border-[#e8e0db] text-[#674936] font-semibold text-sm sm:text-base hover:bg-[#ede9e7] hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <span className="text-2xl shrink-0">{shortcut.icon}</span>
              <span className="break-words text-center">{shortcut.label}</span>
            </button>
          ))}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {dashboardContent.stats.map((stat, index) => (
            <div
              key={index}
              className={`relative min-w-0 ${stat.bgColor} rounded-xl p-5 sm:p-6 shadow-md transform hover:-translate-y-2 transition-all duration-300 group ${stat.view ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={() => stat.view && handleNavigate(stat.view)}
            >
              <div className={`absolute top-0 right-0 w-16 h-16 ${stat.color} opacity-20 rounded-bl-full`}></div>
              <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                <span className={`text-3xl sm:text-4xl p-2 sm:p-3 rounded-full shrink-0 ${stat.color}`}>{stat.icon}</span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-gray-700 font-semibold break-words">{stat.label}</h3>
                  {stat.value !== undefined && (
                    <p className="text-2xl sm:text-3xl font-bold text-gray-800 tabular-nums">{stat.value}</p>
                  )}
                  {stat.categoryFilterKey && (
                    <select
                      value={stat.selectedCategory || 'all'}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        handleCategoryFilterChange(stat.categoryFilterKey, event.target.value);
                      }}
                      className="mt-3 w-full max-w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-[#674936] cursor-pointer"
                      aria-label={`${stat.label} category filter`}
                    >
                      <option value="all">All</option>
                      {stat.useGroupedOptions ? (
                        <>
                          <optgroup label="Leave Categories">
                            {(stat.categoryOptions || [])
                              .filter((option) => option.group === 'category')
                              .map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                          </optgroup>
                          <optgroup label="Leave Types">
                            {(stat.categoryOptions || [])
                              .filter((option) => option.group === 'leave_type')
                              .map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                          </optgroup>
                        </>
                      ) : (
                        (stat.categoryOptions || []).map((option) => (
                          <option key={option.value || option.name} value={option.value || option.name}>
                            {option.label || option.name}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Currency Info */}
        <div className="bg-white rounded-xl shadow-md p-4 text-center mb-6 sm:mb-8">
          <p className="text-gray-600 text-sm">💰 All amounts are displayed in <span className="font-semibold text-blue-600">Kuwaiti Dinar (KWD)</span></p>
        </div>

      </div>

    </div>
  );
};

const PayslipManagement = ({ userProfile }) => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    const handler = setTimeout(() => {
    if (userProfile?.role === 'staff') {
      // Staff can only view their own payslips
      fetchPayslipsForUser();
    } else {
      // Manager/Admin need to select employee
      fetchEmployees();
    }
    }, 300);
    return () => clearTimeout(handler);
  }, [userProfile]);

  const fetchEmployees = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers
      });
      const employeeList = response.data.employees || [];
      setEmployees(employeeList);
      
      // Set default selected employee for manager/admin
      if (employeeList.length > 0) {
        setSelectedEmployee(employeeList[0].id);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchPayslipsForUser = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payslips`, {
        headers
      });
      setPayslips(response.data.payslips || []);
    } catch (error) {
      console.error('Error fetching payslips:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayslipsForEmployee = async (employeeId) => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payslips/${employeeId}`, {
        headers
      });
      setPayslips(response.data.payslips || []);
    } catch (error) {
      console.error('Error fetching payslips:', error);
      alert('Error loading payslips. You may not have permission to view this employee\'s data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedEmployee && userProfile?.role !== 'staff') {
      fetchPayslipsForEmployee(selectedEmployee);
    }
  }, [selectedEmployee, userProfile]);

  const getPageTitle = () => {
    switch (userProfile?.role) {
      case 'admin':
      case 'super': return 'All Employee Payslips';
      case 'manager': return 'Team Payslips';
      case 'staff': return 'My Payslips';
      default: return 'Payslips';
    }
  };

  if (loading) {
    return <div className="loading">Loading payslips...</div>;
  }

  return (
    <div className="payslip-management">
      <div className="section-header">
        <h2>{getPageTitle()}</h2>
        {userProfile?.role !== 'staff' && employees.length > 0 && (
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="employee-select"
          >
            {employees.map(employee => (
              <option key={employee.id} value={employee.id}>
                {employee.name} ({employee.employee_no})
              </option>
            ))}
          </select>
        )}
      </div>

      {userProfile?.role !== 'staff' && (
        <div className="access-note">
          <p>📋 Showing payslips for: <strong>{employees.find(e => e.id === selectedEmployee)?.name || 'Selected Employee'}</strong></p>
        </div>
      )}

      <div className="payslip-grid">
        {payslips.map((payslip) => (
          <div key={payslip.id} className="payslip-card">
            <h3>Pay Period: {payslip.pay_period}</h3>
            <div className="payslip-details">
              <div className="salary-section">
                <h4>Basic Salary</h4>
                <p className="amount">{payslip.basic_salary_formatted || formatCurrency(payslip.basic_salary)}</p>
              </div>
              
              <div className="earnings-section">
                <h4>Earnings</h4>
                {payslip.earnings?.map((earning, index) => (
                  <p key={index}>
                    {earning.type}: {earning.amount_formatted || formatCurrency(earning.amount)}
                  </p>
                ))}
              </div>
              
              <div className="deductions-section">
                <h4>Deductions</h4>
                {payslip.deductions?.map((deduction, index) => (
                  <p key={index}>
                    {deduction.type}: -{deduction.amount_formatted || formatCurrency(deduction.amount)}
                  </p>
                ))}
              </div>
              
              <div className="totals-section">
                <p><strong>Gross Pay: {payslip.gross_pay_formatted || formatCurrency(payslip.gross_pay)}</strong></p>
                <p><strong>Net Pay: {payslip.net_pay_formatted || formatCurrency(payslip.net_pay)}</strong></p>
              </div>
            </div>
            <p className="generated-date">Generated: {formatUsDateFromDateTime(payslip.generated_date) || formatUsDateFromIso(payslip.generated_date) || '—'}</p>
            {payslip.approved_by && (
              <p className="approved-by">Approved by: {payslip.approved_by}</p>
            )}
          </div>
        ))}
      </div>
      
      {payslips.length === 0 && (
        <div className="no-data">
          <p>No payslips found for the selected period.</p>
        </div>
      )}
    </div>
  );
};

// Main App component
const AppContent = () => {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, instance, inProgress } = useMsal();
  const [currentView, setCurrentView] = useState('dashboard');
  const [userProfile, setUserProfile] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [pendingNotificationAction, setPendingNotificationAction] = useState(null);


  useEffect(() => {
    const demoToken = localStorage.getItem('demo-token');
    const demoRole = localStorage.getItem('demo-role');
    if (demoToken && demoRole) {
      setDemoMode(true);
      fetchUserProfile();
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Re-check authentication state when component mounts
    const demoToken = localStorage.getItem('demo-token');
    if (demoToken) {
      setDemoMode(true);
      fetchUserProfile();
    }
  }, [demoMode]);

  // Fetch user profile when authenticated with Azure AD
  useEffect(() => {
    if (isAuthenticated && accounts.length > 0 && !demoMode) {
      console.log('Azure AD user authenticated, fetching profile...');
      fetchUserProfile();
    }
  }, [isAuthenticated, accounts]);

  useEffect(() => {
    const handleNavigate = (event) => {
      if (typeof event.detail === 'string') {
        setCurrentView(event.detail);
      }
    };

    const handleNotificationNavigate = (event) => {
      const detail = event.detail;
      if (!detail?.type || !detail?.entityId) {
        return;
      }

      setPendingNotificationAction(detail);

      if (isHrNotificationType(detail.type)) {
        setCurrentView(provisionalHrView(userProfile));
        return;
      }

      const targetView = resolveTargetView(detail, userProfile);
      if (targetView) {
        setCurrentView(targetView);
      }
    };

    const handleNotificationInfo = (event) => {
      if (event.detail) {
        alert(event.detail);
      }
    };

    window.addEventListener('navigate', handleNavigate);
    window.addEventListener('notification-navigate', handleNotificationNavigate);
    window.addEventListener('notification-info', handleNotificationInfo);

    return () => {
      window.removeEventListener('navigate', handleNavigate);
      window.removeEventListener('notification-navigate', handleNotificationNavigate);
      window.removeEventListener('notification-info', handleNotificationInfo);
    };
  }, [userProfile]);

  const clearBrowserCache = () => {
    // Clear all local storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear browser cache if possible
    if ('caches' in window) {
      caches.keys().then(function(names) {
        names.forEach(function(name) {
          caches.delete(name);
        });
      });
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const profileRequestHeaders = (headers) => ({
    ...headers,
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  });

  const INACTIVE_USER_LOGIN_MESSAGE =
    'User is Inactive, please contact HR department';

  const isInactiveUserDetail = (detail) => {
    if (typeof detail !== 'string') return false;
    const normalized = detail.toLowerCase();
    return (
      normalized.includes('user is inactive') ||
      normalized.includes('employee status is exited') ||
      normalized.includes('employee status is inactive')
    );
  };

  const denyAccessAndLogout = async (detail) => {
    const message = isInactiveUserDetail(detail)
      ? INACTIVE_USER_LOGIN_MESSAGE
      : (detail || 'Access role is not granted, please contact the system administrator');
    console.warn('User not authorized for this system:', message);
    alert(message);
    await handleLogout();
  };

  const loadProfileFromDb = async (headers) => {
    const response = await axios.get(`${backendUrl}/api/profile`, {
      headers: profileRequestHeaders(headers),
    });
    if (response.data?.user) {
      setUserProfile(response.data.user);
      return true;
    }
    return false;
  };

  const syncProfileFromDynamics = async (headers) => {
    const response = await axios.post(`${backendUrl}/api/profile/sync`, {}, {
      headers: profileRequestHeaders(headers),
    });
    if (response.data?.user) {
      setUserProfile(response.data.user);
    }
    return response;
  };

  const fetchUserProfile = async () => {
    if (isDemoMode()) {
      const demoRole = localStorage.getItem('demo-role') || 'staff';
      setUserProfile({ role: demoRole, name: 'Demo User' });
      return;
    }

    try {
      const headers = await getAuthHeaders();
      let profileLoaded = false;

      try {
        profileLoaded = await loadProfileFromDb(headers);
      } catch (profileError) {
        const status = profileError.response?.status;
        const detail = profileError.response?.data?.detail;

        if (status === 401) {
          await denyAccessAndLogout(detail);
          return;
        }

        if (status === 403) {
          // Inactive employees are blocked server-side; do not attempt Dynamics sync.
          if (isInactiveUserDetail(detail)) {
            await denyAccessAndLogout(detail);
            return;
          }

          try {
            await syncProfileFromDynamics(headers);
            profileLoaded = await loadProfileFromDb(headers);
          } catch (syncError) {
            const syncStatus = syncError.response?.status;
            const syncDetail = syncError.response?.data?.detail;
            if (syncStatus === 401 || syncStatus === 403) {
              await denyAccessAndLogout(syncDetail);
            } else {
              console.error('Error syncing profile for new user:', syncError);
            }
            return;
          }

          if (!profileLoaded) {
            await denyAccessAndLogout(
              'You don\'t have access to this system. Please contact the administrator.'
            );
          }
          return;
        }

        console.error('Error loading profile from database:', profileError);
      }

      try {
        await syncProfileFromDynamics(headers);
      } catch (syncError) {
        const syncStatus = syncError.response?.status;
        const syncDetail = syncError.response?.data?.detail;

        if (syncStatus === 401) {
          await denyAccessAndLogout(syncDetail);
          return;
        }

        if (syncStatus === 403 && isInactiveUserDetail(syncDetail)) {
          await denyAccessAndLogout(syncDetail);
          return;
        }

        if (!profileLoaded && syncStatus === 403) {
          await denyAccessAndLogout(syncDetail);
          return;
        }

        console.warn(
          'Background profile sync failed; using cached profile:',
          syncDetail || syncError.message
        );
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      if (error.response?.status === 401) {
        await denyAccessAndLogout(error.response?.data?.detail);
      }
    }
  };

  const handleRefresh = async () => {
    if (isDemoMode()) {
      return;
    }

    try {
      setIsRefreshing(true);
      const headers = await getAuthHeaders();

      try {
        await loadProfileFromDb(headers);
      } catch (profileError) {
        if (profileError.response?.status !== 403) {
          console.warn('Could not refresh profile from database:', profileError.message);
        }
      }

      await syncProfileFromDynamics(headers);
    } catch (error) {
      console.error('Error refreshing user profile:', error);
      if (error.response?.status === 401) {
        await denyAccessAndLogout(error.response?.data?.detail);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    try {
      setDemoMode(false);
      setUserProfile(null);
      setCurrentView('dashboard');

      if (isAuthenticated && accounts.length > 0) {
        await logoutWithMicrosoft(instance, accounts[0]);
        if (isMobileDevice()) {
          return;
        }
      }

      clearBrowserCache();
      window.location.href = window.location.origin;
    } catch (error) {
      console.error('Logout error:', error);
      clearBrowserCache();
      window.location.href = window.location.origin;
    }
  };

  if (loading || isAuthInteractionInProgress(inProgress)) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Signing you in...</p>
        </div>
      </div>
    );
  }

  // Show login screen only if not authenticated AND not in demo mode AND not loading
  if (!isAuthenticated && !demoMode) {
    return <LoginButton />;
  }

  const user = accounts[0] || { name: userProfile?.name || 'Demo User' };

  const handleNotificationActionHandled = () => {
    setPendingNotificationAction(null);
  };

  const renderContent = () => {
    const notificationProps = {
      pendingNotificationAction,
      onNotificationActionHandled: handleNotificationActionHandled,
      onNavigateView: setCurrentView,
    };

    switch (currentView) {
      case 'dashboard':
        return <Dashboard userProfile={userProfile} />;
      case 'employees':
        return (
          <EmployeeManagement
            userProfile={userProfile}
            currentView={currentView}
            {...notificationProps}
          />
        );
      case 'profile':
        return <ProfilePage userProfile={userProfile} currentView={currentView}/>;
      case 'organization':
        return <OrganizationChart userProfile={userProfile} />;
      case 'requests':
        return (
          <RequestManagement
            userProfile={userProfile}
            currentView={currentView}
            {...notificationProps}
          />
        );
      case 'my_leave_requests':
        return (
          <RequestManagement
            userProfile={userProfile}
            currentView={currentView}
            {...notificationProps}
          />
        );
      case 'admin_requests':
        return (
          <RequestManagement
            userProfile={userProfile}
            currentView={currentView}
            {...notificationProps}
          />
        );
      case 'my_admin_requests':
        return (
          <RequestManagement
            userProfile={userProfile}
            currentView={currentView}
            {...notificationProps}
          />
        );
      case 'my_team_requests':
        return (
          <RequestManagement
            userProfile={userProfile}
            currentView={currentView}
            {...notificationProps}
          />
        );
      case 'deleted_requests':
        return (
          <RequestManagement
            userProfile={userProfile}
            currentView={currentView}
            {...notificationProps}
          />
        );
      case 'payslips':
        return <PayslipManagement userProfile={userProfile} />;
      case 'certificates':
        return <CertificateManagement userProfile={userProfile} />;
      case 'positions':
        return <PositionsManagement userProfile={userProfile} />;
      case 'checkInOut':
        return <CheckInCheckOut userProfile={userProfile} />
      case 'allCheckInOut':
        return <AllCheckInOut userProfile={userProfile} />
      case 'announcements':
        return (
          <Announcements
            userProfile={userProfile}
            {...notificationProps}
          />
        );
      case 'policies_trainings':
        return (
          <PoliciesTrainings
            userProfile={userProfile}
            {...notificationProps}
          />
        );
      case 'locations':
        return <LocationManagement userProfile={userProfile} />
      case 'shifts':
        return <ShiftsManagement userProfile={userProfile} />
      case 'grades':
        return <GradesManagement userProfile={userProfile} />
      default:
        return <Dashboard userProfile={userProfile} />;
    }
  };

  const getNavLabel = (section) => {
    if (!userProfile?.role) return section;
    
    switch (section) {
      case 'employees':
        return userProfile.role === 'staff' ? 'My Profile' : 
               userProfile.role === 'manager' ? 'My Team' : 'Employees';
      case 'profile':
        return userProfile.role === 'manager' || hasAdminPrivileges(userProfile.role)
          ? 'My Profile'
          : null;
      case 'my_leave_requests':
        return 'My Leave Requests';
      case 'my_admin_requests':
        return 'My Admin Requests';
      case 'requests':
        return (userProfile.role === 'staff' || userProfile.role === 'manager')  ? 'My Leave Requests' : 'HR Requests';
      case 'payslips':
        return userProfile.role === 'staff' ? 'My Payslips' : 'Payslips';
      case 'certificates':
        return userProfile.role === 'staff' ? 'My Certificates' : 'Certificate Requests';
      default:
        return section;
    }
  };

  return (
    <div className="app min-w-0">
    <App_Header
        user={user}
        isAuthenticated={isAuthenticated}
        demoMode={demoMode}
        handleLogout={handleLogout}
        userProfile={userProfile}
        handleRefresh={handleRefresh}
        clearBrowserCache={clearBrowserCache}
        isRefreshing={isRefreshing}
      />
      <App_Navbar
        userProfile={userProfile}
        currentView={currentView}
        setCurrentView={setCurrentView}
        getNavLabel={getNavLabel}
      />

      <main className="app-main">
        {renderContent()}
      </main>
    </div>
  );
};

// Root App component with MSAL Provider
const App = () => {
  return (
    <MsalProvider instance={msalInstance}>
      <AppContent />
    </MsalProvider>
  );
};

export default App;
